import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {useDeleteUserAbsenceMutation, useLazyGetUserAbsencesQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {catchUnhandledDataGridActions, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {RequiredTestIdProps} from 'shared/web';

import {USER_ABSENCE_GRID_CODE, USER_ABSENCE_OVERVIEW_GRID_CODE} from '../constants/constants';
import {openEditUserAbsenceDialog} from '../utils/openUserAbsenceDialog';

interface UserAbsenceActionParams {
  actionKey: string;
  userId: string | undefined;
  userAbsenceId: string | undefined;
  canUpdate: boolean;
  canDelete: boolean;
}

export function useUserAbsenceActions(props: RequiredTestIdProps) {
  const [deleteUserAbsence] = useDeleteUserAbsenceMutation();
  const [getUserAbsences] = useLazyGetUserAbsencesQuery();

  return ({actionKey, userId, userAbsenceId, canUpdate, canDelete}: UserAbsenceActionParams) => {
    if (isNil(userId) || isNil(userAbsenceId)) {
      showNotification.error();
      return;
    }

    match(actionKey)
      .with('detail', 'edit', () => {
        if (!canUpdate) {
          showNotification.error(i18n.t('general.labels.noPermission'));
          return;
        }

        getUserAbsences({userId})
          .unwrap()
          .then((absences) => {
            const absence = absences.find((item) => item.id === userAbsenceId);

            if (!absence) {
              showNotification.error();
              return;
            }

            openEditUserAbsenceDialog({userId, absence, 'data-testid': props['data-testid']});
          })
          .catch(handleApiError);
      })
      .with('delete', () => {
        if (!canDelete) {
          showNotification.error(i18n.t('general.labels.noPermission'));
          return;
        }

        openDeleteDialog({
          onConfirm: () =>
            deleteUserAbsence({userId, userAbsenceId})
              .unwrap()
              .then(() => {
                refreshDmsDatagrid(USER_ABSENCE_GRID_CODE);
                refreshDmsDatagrid(USER_ABSENCE_OVERVIEW_GRID_CODE);
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
              })
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };
}
